import { AccessLevel, AccessLevels, isMinFls } from "../../pojo/Fls";
import { RoleRights } from "./securityConfig/role.rights";
import { RoleRightsAccessService } from "./role.rights.access.service";

export class SeAccessService {
    /**
     * Check if user has at least minimum access to a right for specified organizations
     *
     * @param rightName The right to check
     * @param orgIds Array of organization IDs to check
     * @param minLevel Minimum access level required (defaults to Read)
     * @returns Map of orgId -> boolean indicating access for each org
     * if (access[123]) { // User has at least Read access to SE_BUDGET for org 123
     */
    public static async hasAccess(
        rightName: RoleRights.RoleRightsList,
        orgIds: number[],
        minLevel: AccessLevels = AccessLevels.Read,
    ): Promise<Record<number, boolean>> {
        const rights = await RoleRightsAccessService.getSeRights(orgIds);
        const result: Record<number, boolean> = {};

        orgIds.forEach((orgId) => {
            const orgRights = rights.orgRights?.[orgId];
            if (!orgRights) {
                result[orgId] = false;
            } else {
                const actualLevel = orgRights[rightName] || AccessLevels.None;
                result[orgId] = isMinFls(actualLevel as AccessLevels, minLevel);
            }
        });

        return result;
    }

    /**
     * Get the actual access level for a right for specified organizations
     *
     * @param rightName The right to check
     * @param orgIds Array of organization IDs to check
     * @returns Map of orgId -> AccessLevel for each org
     * // Returns: { 123: "F", 456: "R" }
     */
    public static async getAccessLevel(
        rightName: RoleRights.RoleRightsList,
        orgIds: number[],
    ): Promise<Record<number, AccessLevel>> {
        const rights = await RoleRightsAccessService.getSeRights(orgIds);
        const result: Record<number, AccessLevel> = {};

        orgIds.forEach((orgId) => {
            const orgRights = rights.orgRights?.[orgId];
            result[orgId] = (orgRights?.[rightName] || AccessLevels.None) as AccessLevel;
        });

        return result;
    }

    /**
     * Check if user has access to ANY of the specified rights for specified organizations
     *
     * @param rightNames Array of rights to check
     * @param orgIds Array of organization IDs to check
     * @param minLevel Minimum access level required (defaults to Read)
     * @returns Map of orgId -> boolean (true if user has access to ANY right)
     */
    public static async hasAccessToAny(
        rightNames: RoleRights.RoleRightsList[],
        orgIds: number[],
        minLevel: AccessLevels = AccessLevels.Read,
    ): Promise<Record<number, boolean>> {
        const rights = await RoleRightsAccessService.getSeRights(orgIds);
        const result: Record<number, boolean> = {};

        orgIds.forEach((orgId) => {
            const orgRights = rights.orgRights?.[orgId];
            if (!orgRights) {
                result[orgId] = false;
            } else {
                result[orgId] = rightNames.some((rightName) => {
                    const actualLevel = orgRights[rightName] || AccessLevels.None;
                    return isMinFls(actualLevel as AccessLevels, minLevel);
                });
            }
        });

        return result;
    }

    /**
     * Check if user has access to ALL of the specified rights for specified organizations
     *
     * @param rightNames Array of rights to check
     * @param orgIds Array of organization IDs to check
     * @param minLevel Minimum access level required (defaults to Read)
     * @returns Map of orgId -> boolean (true if user has access to ALL rights)
     */
    public static async hasAccessToAll(
        rightNames: RoleRights.RoleRightsList[],
        orgIds: number[],
        minLevel: AccessLevels = AccessLevels.Read,
    ): Promise<Record<number, boolean>> {
        const rights = await RoleRightsAccessService.getSeRights(orgIds);
        const result: Record<number, boolean> = {};

        orgIds.forEach((orgId) => {
            const orgRights = rights.orgRights?.[orgId];
            if (!orgRights) {
                result[orgId] = false;
            } else {
                result[orgId] = rightNames.every((rightName) => {
                    const actualLevel = orgRights[rightName] || AccessLevels.None;
                    return isMinFls(actualLevel as AccessLevels, minLevel);
                });
            }
        });

        return result;
    }

    /**
     * Get access levels for multiple rights across specified organizations
     *
     * @param rightNames Array of rights to check
     * @param orgIds Array of organization IDs to check
     * @returns Map of orgId -> Map of rightName -> AccessLevel
     * // Returns: { 123: { SE_BUDGET: "R", SE_ORG_EDIT: "F" } }
     */
    public static async getAccessLevels(
        rightNames: RoleRights.RoleRightsList[],
        orgIds: number[],
    ): Promise<Record<number, Record<RoleRights.RoleRightsList, AccessLevel>>> {
        const rights = await RoleRightsAccessService.getSeRights(orgIds);
        const result: Record<number, Record<string, AccessLevel>> = {};

        orgIds.forEach((orgId) => {
            const orgRights = rights.orgRights?.[orgId];
            const orgResult: Record<string, AccessLevel> = {};

            rightNames.forEach((rightName) => {
                orgResult[rightName] = (orgRights?.[rightName] || AccessLevels.None) as AccessLevel;
            });

            result[orgId] = orgResult;
        });

        return result as Record<number, Record<RoleRights.RoleRightsList, AccessLevel>>;
    }
}
